import React, { useState } from 'react';

import PropTypes from 'prop-types';

import { AssetDownloadItem } from 'components/AssetDownloadItem/AssetDownloadItem';
import {
  ExpandedConsentSections,
  getDefaultOperatorSelections,
} from 'components/DataPolicy/ExpandedConsentSections';
import { FormSection } from 'components/FormHelper/FormSection';

import { TENANT_ID } from 'global/constants';

import { getTenantId } from 'utils/tenantUtil';

const TENANT_NAMES = {
  [TENANT_ID.EDI]: 'E.DIS Netz GmbH',
  [TENANT_ID.SHNG]: 'Schleswig-Holstein Netz GmbH',
  [TENANT_ID.AVA]: 'Avacon Netz GmbH',
  [TENANT_ID.BAG]: 'Bayernwerk Netz GmbH',
  [TENANT_ID.LVN]: 'LEW Verteilnetz GmbH',
  [TENANT_ID.MTN]:
    'Mitteldeutsche Netzgesellschaft Strom mbH, Mitteldeutsche Netzgesellschaft Gas mbH und Verteilnetz Plauen GmbH',
};

export const DEFAULT_CONFIRMATION_TEXT =
  'Mit Absenden dieser Anfrage bestätige ich / bestätigen wir, dass ich / wir die „Datenschutz“-Zustimmung und die hier aktuell hinterlegte „Informationen zum Datenschutz“ zur Kenntnis genommen habe / haben und mit Absenden dieser Anfrage bestätige ich / bestätigen wir, dass die oben unter „Inhaber / Gesellschafter / Teilhaber“ und unter „verantwortliche Fachkräfte“ genannten Personen die „Informationen zum Datenschutz“ gelesen haben und mit den in der „Datenschutz“-Zustimmung genannten Punkten einverstanden sind.';

export const DataPolicyContent = ({
  className,
  confirmationText,
  dsgvoAddressPublic,
  dsgvoCanSendMail,
  onChangeDsvgoAddressPublic,
  onChangeDsvgoCanSendMail,
  dsgvoAddressPublicName,
  dsgvoCanSendMailName,
  documents,
  shngDocuments,
  showAddressPublicApproval,
  showConsentSection,
  currentBusinessUnit,
  informationOperators,
  addressOperators,
  onOperatorSelectionsChange,
}) => {
  const tenantId = getTenantId();
  const tenantName = TENANT_NAMES[tenantId] || TENANT_NAMES[TENANT_ID.EDI];
  const [localInformationOperators, setLocalInformationOperators] = useState(() =>
    getDefaultOperatorSelections(tenantId)
  );
  const [localAddressOperators, setLocalAddressOperators] = useState(() =>
    getDefaultOperatorSelections(tenantId)
  );
  const selectedInformationOperators = informationOperators || localInformationOperators;
  const selectedAddressOperators = addressOperators || localAddressOperators;
  const filteredDocuments = documents.filter(({ link, label }) => link && label);
  const filteredShngDocuments = shngDocuments.filter(({ link, label }) => link && label);

  const handleConsentChange = ({
    information,
    address,
    informationOperators: nextInformationOperators,
    addressOperators: nextAddressOperators,
  }) => {
    setLocalInformationOperators(nextInformationOperators);
    setLocalAddressOperators(nextAddressOperators);
    onOperatorSelectionsChange({
      informationOperators: nextInformationOperators,
      addressOperators: nextAddressOperators,
    });
    onChangeDsvgoCanSendMail({
      target: { name: dsgvoCanSendMailName, value: information },
    });
    onChangeDsvgoAddressPublic({
      target: { name: dsgvoAddressPublicName, value: address },
    });
  };

  return (
    <div className={className}>
      {tenantId === TENANT_ID.SHNG && filteredShngDocuments.length > 0 && (
        <FormSection subtitle="Grundsätze der Zusammenarbeit">
          <div className="data-policy__down-section-wrapper">
            {filteredShngDocuments.map(({ link, label }) => (
              <AssetDownloadItem key={label} asset={link} name={label} />
            ))}
          </div>
        </FormSection>
      )}
      {filteredDocuments.length > 0 && (
        <FormSection subtitle="Informationen zum Datenschutz">
          <div className="data-policy__down-section-wrapper">
            {filteredDocuments.map(({ link, label }) => (
              <AssetDownloadItem key={label} asset={link} name={label} />
            ))}
          </div>
        </FormSection>
      )}
      {tenantId === TENANT_ID.MTN && (
        <ul>
          <li>
            <a
              className="link"
              href="https://www.mitnetz-strom.de/datenschutzinformation"
              target="_blank"
              rel="noopener noreferrer">
              Informationen zum Datenschutz der Mitteldeutschen Netzgesellschaft Strom mbH
            </a>
          </li>
          <li>
            <a
              className="link"
              href="https://www.mitnetz-gas.de/datenschutzinformation"
              target="_blank"
              rel="noopener noreferrer">
              Informationen zum Datenschutz der Mitteldeutsche Netzgesellschaft Gas mbH
            </a>
          </li>
          <li>
            <a
              className="link"
              href="https://www.plauen-netz.de/datenschutzinformation"
              target="_blank"
              rel="noopener noreferrer">
              Informationen zum Datenschutz der Verteilnetz Plauen GmbH
            </a>
          </li>
        </ul>
      )}
      {showConsentSection && (
        <FormSection subtitle="Zustimmungen zum Datenschutz">
          <div>{confirmationText}</div>
          <ExpandedConsentSections
            currentBusinessUnit={currentBusinessUnit}
            showAddress={showAddressPublicApproval}
            values={{
              information: dsgvoCanSendMail,
              address: dsgvoAddressPublic,
              informationOperators: selectedInformationOperators,
              addressOperators: selectedAddressOperators,
            }}
            onChange={handleConsentChange}
          />
        </FormSection>
      )}
    </div>
  );
};

DataPolicyContent.propTypes = {
  className: PropTypes.string,
  confirmationText: PropTypes.string,
  dsgvoAddressPublic: PropTypes.bool.isRequired,
  dsgvoAddressPublicName: PropTypes.string.isRequired,
  dsgvoCanSendMail: PropTypes.bool.isRequired,
  dsgvoCanSendMailName: PropTypes.string.isRequired,
  onChangeDsvgoAddressPublic: PropTypes.func.isRequired,
  onChangeDsvgoCanSendMail: PropTypes.func.isRequired,
  documents: PropTypes.array.isRequired,
  shngDocuments: PropTypes.array,
  showAddressPublicApproval: PropTypes.bool,
  showConsentSection: PropTypes.bool,
  currentBusinessUnit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  informationOperators: PropTypes.arrayOf(PropTypes.string),
  addressOperators: PropTypes.arrayOf(PropTypes.string),
  onOperatorSelectionsChange: PropTypes.func,
};

DataPolicyContent.defaultProps = {
  className: '',
  confirmationText: '',
  shngDocuments: [],
  showAddressPublicApproval: true,
  showConsentSection: true,
  currentBusinessUnit: undefined,
  informationOperators: undefined,
  addressOperators: undefined,
  onOperatorSelectionsChange: () => undefined,
};
